<template>
  <div>
    <base-header class="pb-6" />
    <b-container fluid class="mt--6 max-width">
      <b-card no-body>
        <validation-observer v-slot="{ handleSubmit }" ref="formValidator">
          <b-form role="form" @submit.prevent="handleSubmit(onSubmit)">
            <card-header title="Create Device">
              <base-button type="white" size="sm" @click="onCancel">Cancel</base-button>
              <base-button
                type="success"
                size="sm"
                native-type="submit"
                :loading="isLoading"
              >Create</base-button>
            </card-header>

            <b-card-body>
              <b-row>
                <b-col md="6">
                  <base-input
                    label="Device ID"
                    name="name"
                    v-model="form.name"
                    :rules="{ required: true, devicename: true }"
                  />
                </b-col>
                <b-col md="6">
                  <base-input
                    label="Description"
                    name="description"
                    v-model="form.description"
                  />
                </b-col>
              </b-row>

              <b-row>
                <b-col md="6">
                  <fieldset>
                    <label class="form-control-label">
                      Groups
                      <span class="font-weight-light">Optional</span>
                    </label>
                    <dispenser-select :selected.sync="form.group" />
                  </fieldset>
                </b-col>
              </b-row>

                          <!-- LoRa checkbox -->
              <div class="mt-4 mb-2">
                <div class="pl-4">
                  <b-form-checkbox
                    v-model="addToLora"
                    :disabled="!loraAvailable || isLoading"
                    switch
                  >
                    Add this device to LoRa / TTN
                  </b-form-checkbox>
                </div>
                <small v-if="!loraAvailable" class="text-muted ml-5">
                  <i class="fas fa-info-circle mr-1"></i>
                  {{ loraUnavailableReason }}
                </small>
              </div>

              <!-- LoRa settings, only when checkbox is on -->
              <div v-if="addToLora" class="border rounded p-3 mt-3">
                <h6 class="mb-3">
                  <i class="fas fa-broadcast-tower mr-1"></i>
                  LoRa settings
                </h6>

                <b-row>
                  <b-col md="6">
                    <b-form-group label="DevEUI (16 hex)">
                      <b-form-input
                        v-model.trim="form.dev_eui"
                        :placeholder="suggestedDevEui || 'auto from Device ID'"
                        :disabled="isLoading"
                      />
                      <small class="text-muted d-block">
                        Auto-generated from Device ID.
                        <a
                          v-if="suggestedDevEui && form.dev_eui !== suggestedDevEui"
                          href="#"
                          @click.prevent="form.dev_eui = suggestedDevEui"
                        >Use default ({{ suggestedDevEui }})</a>
                        <span v-else-if="!suggestedDevEui">
                          Enter a valid 12-hex Device ID first, or type DevEUI manually.
                        </span>
                      </small>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="JoinEUI (16 hex)">
                      <b-form-input
                        v-model.trim="form.join_eui"
                        :placeholder="defaultJoinEui"
                        :disabled="isLoading"
                      />
                      <small class="text-muted">
                        Default <code>{{ defaultJoinEui }}</code>. Override only if your device requires a different JoinEUI.
                      </small>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-form-group label="AppKey (32 hex)">
                  <b-input-group>
                    <b-form-input
                      v-model="form.app_key"
                      :type="showApiKey ? 'text' : 'password'"
                      placeholder="00112233445566778899aabbccddeeff"
                      autocomplete="new-password"
                      :disabled="isLoading"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="outline-secondary"
                        :disabled="isLoading"
                        @click="showApiKey = !showApiKey"
                      >
                        <i :class="showApiKey ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-muted">
                    Root key. Pre-filled with a default for testing. Stored encrypted.
                  </small>
                </b-form-group>

                <b-row>
                  <b-col md="6">
                    <b-form-group label="Frequency plan">
                      <b-form-select
                        v-model="form.frequency_plan_id"
                        :options="frequencyPlanOptions"
                        :disabled="isLoading"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="LoRaWAN version">
                      <b-form-select
                        v-model="form.lorawan_selection"
                        :options="lorawanVersionOptions"
                        :disabled="isLoading"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-form-checkbox v-model="form.supports_class_c" :disabled="isLoading">
                  Device supports Class C (continuous receive)
                </b-form-checkbox>
              </div>
            </b-card-body>
          </b-form>
        </validation-observer>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import { Routes } from "@/router";
import { Action } from "@/store/actions";
import FileLoader from "@/views/components/FileLoader";
import { MapServiceDevice } from "@/services/map.service";
import DispenserSelect from "../../views/components/DispenserSelect.vue";

const DEVICE_ID_HEX = /^[0-9a-f]{12}$/i;
const DEFAULT_JOIN_EUI = "38b8ebe000000001";

const FREQ_PLANS = [
  { value: "EU_863_870_TTN",   text: "EU_863_870_TTN (Europe)" },
  { value: "US_902_928_FSB_2", text: "US_902_928_FSB_2 (North America)" },
  { value: "AU_915_928_FSB_2", text: "AU_915_928_FSB_2 (Australia)" },
];

const LORAWAN_VERSIONS = [
    { value: "MAC_V1_0_2|PHY_V1_0_2_REV_B", text: "LoRaWAN 1.0.2" },
  { value: "MAC_V1_0_3|PHY_V1_0_3_REV_A", text: "LoRaWAN 1.0.3" },
];

export default {
  name: "DeviceNew",
  components: { FileLoader, DispenserSelect },
  data() {
    return {
      form: {
        name: "",
        description: "",
        manufacturing_type: "CC1",
        lora_deveui: "",
        lte_imsi: "",
        version_id: "",
        group: "",
        dispenser: undefined,
        cert: undefined,
        dev_eui: "",
        join_eui: DEFAULT_JOIN_EUI,
        app_key: "00112233445566778899aabbccddeeff",
        frequency_plan_id: "EU_863_870_TTN",
        lorawan_selection: "MAC_V1_0_2|PHY_V1_0_2_REV_B",
        supports_class_c: true,
      },
      isLoading: false,
      addToLora: false,
      showApiKey: false,
      defaultJoinEui: DEFAULT_JOIN_EUI,
      frequencyPlanOptions: FREQ_PLANS,
      lorawanVersionOptions: LORAWAN_VERSIONS,
    };
  },
  computed: {
    activeEntity() {
      return this.$store.getters.activeEntity;
    },
    loraIntegration() {
      return this.$store.getters.ttnIntegration(this.activeEntity);
    },
    loraAvailable() {
      return !!(this.loraIntegration && this.loraIntegration.status === "ok");
    },
    loraUnavailableReason() {
      if (!this.loraIntegration) {
        return "This entity has no TTN integration yet. Configure it in Entity settings first.";
      }
      return `TTN integration status is '${this.loraIntegration.status}'. Fix and re-test it in Entity settings first.`;
    },
    suggestedDevEui() {
      const n = (this.form.name || "").toLowerCase();
      if (!DEVICE_ID_HEX.test(n)) return "";
      return n.slice(0, 7) + "0" + "0000" + n.slice(-4);
    },
  },
  watch: {
    "form.name"() {
      const suggested = this.suggestedDevEui;
      if (
        suggested &&
        (!this.form.dev_eui || this.form.dev_eui === this._lastSuggested)
      ) {
        this.form.dev_eui = suggested;
        this._lastSuggested = suggested;
      }
    },
    addToLora(on) {
      if (on) {
        if (this.suggestedDevEui && !this.form.dev_eui) {
          this.form.dev_eui = this.suggestedDevEui;
          this._lastSuggested = this.suggestedDevEui;
        }
        if (!this.form.join_eui) this.form.join_eui = DEFAULT_JOIN_EUI;
      }
    },
  },
  methods: {
    onCancel() {
      this.$router.push({ name: Routes.deviceList });
    },
    loraFormValid() {
      const hex = (s, n) =>
        typeof s === "string" &&
        new RegExp(`^[0-9a-f]{${n}}$`, "i").test(s);
      return (
        hex(this.form.dev_eui, 16) &&
        hex(this.form.join_eui, 16) &&
        hex(this.form.app_key, 32)
      );
    },
    async onSubmit() {
      if (this.addToLora && !this.loraFormValid()) {
        this.$notify({
          type: "warning",
          title: "Missing LoRa fields",
          text: "Fill DevEUI (16 hex), JoinEUI (16 hex) and AppKey (32 hex) before saving.",
        });
        return;
      }

      this.isLoading = true;

      try {
        const model = MapServiceDevice.formToModel(this.form);

        if (this.addToLora) {
          const [mac, phy] = this.form.lorawan_selection.split("|");
          Object.assign(model, {
            transport: "lora",
            provider: "ttn",
            dev_eui: (this.form.dev_eui || "").toLowerCase(),
            join_eui: (this.form.join_eui || DEFAULT_JOIN_EUI).toLowerCase(),
            app_key: this.form.app_key,
            frequency_plan_id: this.form.frequency_plan_id,
            lorawan_version: mac,
            lorawan_phy_version: phy,
            supports_class_c: !!this.form.supports_class_c,
            provision_status: "pending",
          });
        } else {
          model.transport = "wifi";
        }

        await this.$store.dispatch(Action.device.add, model);

        if (this.addToLora) {
          try {
            await this.$store.dispatch(Action.ttn.deviceProvision, {
              entity: this.activeEntity,
              name: this.form.name,
            });
            this.$notify({
              type: "success",
              title: "Device created",
              text: "Device created and provisioned in TTN.",
            });
          } catch (err) {
            const msg =
              (err && (err.message || err.provision_detail)) ||
              "Provisioning failed.";
            this.$notify({
              type: "warning",
              title: "Created, TTN provisioning failed",
              text: `${msg} You can retry from the device list.`,
            });
          }
        } else {
          this.$notify({
            type: "success",
            title: "Device created",
            text: "Wi-Fi device created.",
          });
        }

        this.$router.push({ name: Routes.deviceList });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: "Create failed",
          text:
            (error && (error.message || JSON.stringify(error))) ||
            "Unknown error",
        });
        this.isLoading = false;
      }
    },
  },
  created() {
    this._lastSuggested = "";
    if (this.activeEntity) {
      this.$store.dispatch(Action.ttn.load, this.activeEntity).catch(() => {});
    }
  },
};
</script>